<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';

	// Design system components
	import { ButtonV2, Container, InputV2, Loader, Stack, Text } from '@Shelly/shelly-design-system';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import VerivoxAttribution from 'src/components/PromotionalIntegrations/Verivox/shared/VerivoxAttribution.svelte';

	//Energy Price components
	import {
		energyPriceColorSchemes,
		type ColorSchemeKey,
	} from 'src/components/PromotionalIntegrations/Verivox/utils/colorSchemes';
	import {
		energyPriceApi,
		type EnergyLocationData,
	} from 'src/components/PromotionalIntegrations/Verivox/api/verivoxApi';
	import VerivoxSetupModal from 'src/components/PromotionalIntegrations/Verivox/modals/setup/VerivoxSetupModal.svelte';

	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { isEnergyPriceFeatureEnabled } from 'src/helpers/energy_price_helpers';
	import {
		clearAllVerivoxDismissals,
		isVerivoxSurfaceDismissed,
	} from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';

	let locationData: EnergyLocationData | null = null;
	let isClearing = false;
	let currentScheme: ColorSchemeKey = 'scheme1';

	let postalCode = locationData?.postalCode || '';
	let locationString = locationData?.locationString || '';
	let locationStringError = '';

	let editMode = false;
	let setupModalOpen = false;
	let isMounted = false;

	const handleOpenSetupModal = () => {
		setupModalOpen = true;
	};

	const handleEditMode = () => {
		editMode = true;
		handleOpenSetupModal();
	};

	onMount(() => {
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
		locationData = energyPriceApi.getCachedLocation();
		postalCode = locationData?.postalCode || '';
		locationString = locationData?.locationString || locationData?.locationId || '';

		isMounted = true;
	});

	const handleFinishSetup = () => {
		locationData = energyPriceApi.getCachedLocation();
		postalCode = locationData?.postalCode || '';
		locationString = locationData?.locationString || locationData?.locationId || '';
	};

	const handleGetStarted = () => {
		editMode = false;
		handleOpenSetupModal();
	};

	const handleShowBannersAgain = async () => {
		try {
			await clearAllVerivoxDismissals();
			locationData = energyPriceApi.getCachedLocation();
			postalCode = locationData?.postalCode || '';
			locationString = locationData?.locationString || locationData?.locationId || '';
		} catch (error) {
			console.error('Error clearing dismissal state:', error);
		}
	};

	$: featureEnabled = isEnergyPriceFeatureEnabled();
	$: isDismissed =
		isVerivoxSurfaceDismissed('consumption') ||
		isVerivoxSurfaceDismissed('notifications') ||
		isVerivoxSurfaceDismissed('energyDashboard') ||
		isVerivoxSurfaceDismissed('customDashboard');
</script>

{#if featureEnabled}
	{#if !isMounted}
		<div class="text-center">
			<Loader size="m" />
		</div>
	{:else if locationData}
		<Stack directionMobile="row" direction="column" gap="m" class="energy-price-location">
			<div>
				<Text styleType="body" class="mb-s">
					{$format('energy_price_location_title', { default: 'Energy price location' })}
				</Text>
				<Text styleType="body-small" class="text-color-muted">
					{$format('energy_price_location_description', {
						default: 'Use your saved location to compare available electricity tariffs.',
						locale: 'de',
					})}
				</Text>
			</div>

			<div class="energy-price-location__inputs">
				<InputV2
					disabled={true}
					icon="fas fa-map-pin"
					label={capFirstChar($format('label_postal_code', { default: 'Postal Code' }))}
					bind:value={postalCode}
					type="text"
					id="input-wj138ps"
					aria-labelledby="input-wj138ps"
				/>
				<InputV2
					disabled={true}
					error={locationStringError}
					icon="fas fa-map-marker-alt"
					label={capFirstChar($format('label_location', { default: 'Location' }))}
					bind:value={locationString}
					type="text"
					id="input-fhg23cb"
					aria-labelledby="input-fhg23cb"
				/>
			</div>

			<div class="energy-price-location__actions">
				<ButtonV2
					size="m"
					class="width-full width-md-auto"
					variant="secondary"
					on:click={handleEditMode}
					disabled={isClearing}
				>
					{capFirstChar(
						$format('button_change', {
							default: 'change',
						}),
					)}
				</ButtonV2>
				{#if isDismissed}
					<ButtonV2
						size="m"
						class="width-full width-md-auto"
						variant="secondary"
						on:click={handleShowBannersAgain}
						disabled={isClearing}
					>
						{capFirstChar(
							$format('button_show_banners_again', {
								default: 'Show banners again',
								locale: 'de',
							}),
						)}
					</ButtonV2>
				{/if}
			</div>
		</Stack>
	{:else}
		<TransitionWrapper>
			<Container padding="0">
				<div
					class="energy-price-card"
					style="background: {energyPriceColorSchemes.schemes[currentScheme]}"
				>
					<div class="energy-price-card__content">
						<div class="energy-price-card__icon">
							<i class="fas fa-bolt"></i>
						</div>
						<div class="energy-price-card__text">
							<Text styleType="body" class="energy-price-card__title">
								{$format('energy_price_setup_prompt_title', {
									default: 'Günstige Stromtarife entdecken',
									locale: 'de',
								})}
							</Text>
							<Text styleType="body-small" class="energy-price-card__description">
								{$format('energy_price_setup_prompt_message', {
									default: 'Vergleichen Sie verfügbare Stromtarife in Ihrer Region',
									locale: 'de',
								})}
							</Text>
						</div>
						<div class="energy-price-card__action">
							<ButtonV2 size="m" variant="secondary" on:click={handleGetStarted}>
								{$format('button_get_started', {
									default: 'Loslegen',
									locale: 'de',
								})}
							</ButtonV2>
						</div>
					</div>
					<div class="energy-price-card__footer">
						<VerivoxAttribution />
					</div>
				</div>
			</Container>
		</TransitionWrapper>
	{/if}

	<VerivoxSetupModal bind:showModal={setupModalOpen} {editMode} on:finishSetup={handleFinishSetup} />
{/if}

<style>
	.energy-price-location__inputs {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
	}

	.energy-price-location__actions {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
	}

	.energy-price-card {
		border-radius: 16px;
		padding: 16px;
		box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
		position: relative;
		overflow: hidden;
		transition:
			box-shadow 0.2s ease,
			transform 0.2s ease;
	}

	.energy-price-card::before {
		content: '';
		position: absolute;
		inset: auto -32px -48px auto;
		width: 144px;
		height: 144px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 50%;
		pointer-events: none;
	}

	.energy-price-card:hover {
		transform: translateY(-1px);
		box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
	}

	.energy-price-card__content {
		position: relative;
		z-index: 1;
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 12px;
	}

	.energy-price-card__icon {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #ffffff;
		font-size: 22px;
	}

	.energy-price-card__text {
		flex: 1;
		min-width: 0;
	}

	.energy-price-card__title {
		margin-bottom: 4px;
		color: #ffffff;
	}

	.energy-price-card__description {
		color: rgba(255, 255, 255, 0.9);
	}

	.energy-price-card__action {
		flex-shrink: 0;
	}

	.energy-price-card__footer {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		margin-top: 8px;
		position: relative;
		z-index: 1;
	}

	:global(.attribution-text) {
		opacity: 0.7;
		text-align: right;
		color: rgba(255, 255, 255, 0.8) !important;
		font-size: 9px;
		line-height: 1.3;
	}

	@media (max-width: 768px) {
		.energy-price-location__inputs {
			grid-template-columns: 1fr;
		}

		.energy-price-location__actions {
			flex-direction: column;
		}

		.energy-price-card {
			padding: 14px;
		}

		.energy-price-card__content {
			flex-wrap: wrap;
		}

		.energy-price-card__icon {
			width: 44px;
			height: 44px;
			font-size: 20px;
		}

		.energy-price-card__action {
			width: 100%;
		}

		.energy-price-card__action :global(button) {
			width: 100%;
		}
	}
</style>
